import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, RefreshCw, Loader2, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { getAuthHeaders } from '@/lib/authHeaders';
import { useEventTracking } from '@/hooks/useEventTracking';
import type { TranslationLanguage } from './MessageLanguageSelector';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const LANG_NAMES: Record<string, string> = {
  en: 'English', es: 'Español', fr: 'Français', pt: 'Português', it: 'Italiano', de: 'Deutsch',
};

const copy = {
  en: {
    title: 'Review Translation',
    subtitle: 'Compare your original message with the translated version.',
    original: 'Original',
    translated: 'Translated',
    useTranslated: 'Use translated version',
    keepOriginal: 'Keep original version',
    editOriginal: 'Edit original',
    regenerate: 'Regenerate translation',
    translating: 'Translating your message...',
    failed: 'Translation failed. Please try again.',
    recipientNote: 'Recipient will receive the translated version.',
  },
  es: {
    title: 'Revisar Traducción',
    subtitle: 'Compara tu mensaje original con la versión traducida.',
    original: 'Original',
    translated: 'Traducido',
    useTranslated: 'Usar versión traducida',
    keepOriginal: 'Mantener versión original',
    editOriginal: 'Editar original',
    regenerate: 'Regenerar traducción',
    translating: 'Traduciendo tu mensaje...',
    failed: 'La traducción falló. Inténtalo de nuevo.',
    recipientNote: 'El destinatario recibirá la versión traducida.',
  },
  fr: {
    title: 'Vérifier la Traduction',
    subtitle: 'Comparez votre message original avec la version traduite.',
    original: 'Original',
    translated: 'Traduit',
    useTranslated: 'Utiliser la version traduite',
    keepOriginal: "Garder la version originale",
    editOriginal: "Modifier l'original",
    regenerate: 'Régénérer la traduction',
    translating: 'Traduction en cours...',
    failed: 'La traduction a échoué. Réessayez.',
    recipientNote: 'Le destinataire recevra la version traduite.',
  },
  pt: {
    title: 'Revisar Tradução',
    subtitle: 'Compare sua mensagem original com a versão traduzida.',
    original: 'Original',
    translated: 'Traduzido',
    useTranslated: 'Usar versão traduzida',
    keepOriginal: 'Manter versão original',
    editOriginal: 'Editar original',
    regenerate: 'Regenerar tradução',
    translating: 'Traduzindo sua mensagem...',
    failed: 'Tradução falhou. Tente novamente.',
    recipientNote: 'O destinatário receberá a versão traduzida.',
  },
  ar: {
    title: 'مراجعة الترجمة',
    subtitle: 'قارن رسالتك الأصلية مع النسخة المترجمة.',
    original: 'الأصلي',
    translated: 'مترجم',
    useTranslated: 'استخدم النسخة المترجمة',
    keepOriginal: 'احتفظ بالنسخة الأصلية',
    editOriginal: 'تعديل الأصلي',
    regenerate: 'إعادة الترجمة',
    translating: 'جارٍ ترجمة رسالتك...',
    failed: 'فشلت الترجمة. حاول مرة أخرى.',
    recipientNote: 'سيتلقى المستلم النسخة المترجمة.',
  },
  ja: {
    title: '翻訳の確認',
    subtitle: '原文と翻訳版を比較してください。',
    original: '原文',
    translated: '翻訳',
    useTranslated: '翻訳版を使用',
    keepOriginal: '原文を使用',
    editOriginal: '原文を編集',
    regenerate: '翻訳を再生成',
    translating: 'メッセージを翻訳中...',
    failed: '翻訳に失敗しました。もう一度お試しください。',
    recipientNote: '受信者は翻訳版を受け取ります。',
  },
  ko: {
    title: '번역 확인',
    subtitle: '원본 메시지와 번역된 버전을 비교하세요.',
    original: '원본',
    translated: '번역',
    useTranslated: '번역 버전 사용',
    keepOriginal: '원본 버전 유지',
    editOriginal: '원본 수정',
    regenerate: '번역 재생성',
    translating: '메시지 번역 중...',
    failed: '번역 실패. 다시 시도하세요.',
    recipientNote: '수신자는 번역된 버전을 받습니다.',
  },
  id: {
    title: 'Tinjau Terjemahan',
    subtitle: 'Bandingkan pesan asli Anda dengan versi terjemahan.',
    original: 'Asli',
    translated: 'Terjemahan',
    useTranslated: 'Gunakan versi terjemahan',
    keepOriginal: 'Pertahankan versi asli',
    editOriginal: 'Edit asli',
    regenerate: 'Regenerasi terjemahan',
    translating: 'Menerjemahkan pesan Anda...',
    failed: 'Terjemahan gagal. Coba lagi.',
    recipientNote: 'Penerima akan menerima versi terjemahan.',
  },

  hi: {
    title: 'अनुवाद की समीक्षा करें',
    subtitle: 'अपने मूल संदेश की अनुवादित संस्करण से तुलना करें।',
    original: 'मूल',
    translated: 'अनुवादित',
    useTranslated: 'अनुवादित संस्करण का उपयोग करें',
    keepOriginal: 'मूल संस्करण रखें',
    editOriginal: 'मूल संपादित करें',
    regenerate: 'अनुवाद पुनः बनाएँ',
    translating: 'आपके संदेश का अनुवाद हो रहा है...',
    failed: 'अनुवाद विफल रहा। कृपया पुनः प्रयास करें।',
    recipientNote: 'प्राप्तकर्ता को अनुवादित संस्करण प्राप्त होगा।',
  },
};

interface TranslationReviewProps {
  originalMessage: string;
  writingLanguage: TranslationLanguage;
  recipientLanguage: TranslationLanguage;
  template: string;
  onUseTranslated: (translated: string) => void;
  onKeepOriginal: () => void;
  onEditOriginal: () => void;
}

export function TranslationReview({
  originalMessage,
  writingLanguage,
  recipientLanguage,
  template,
  onUseTranslated,
  onKeepOriginal,
  onEditOriginal,
}: TranslationReviewProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const { trackEvent } = useEventTracking();
  const [translatedMessage, setTranslatedMessage] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [hasTranslated, setHasTranslated] = useState(false);

  const translate = useCallback(async () => {
    setIsTranslating(true);
    trackEvent('translation_requested', { from: writingLanguage, to: recipientLanguage, template });

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/announcement-ai-assist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: 'translate_message',
          message: originalMessage,
          sourceLanguage: writingLanguage,
          targetLanguage: recipientLanguage,
          template,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) { toast.error(c.failed); return; }
        throw new Error('Translation failed');
      }

      const data = await response.json();
      if (data.translatedMessage) {
        setTranslatedMessage(data.translatedMessage);
        setHasTranslated(true);
      } else {
        toast.error(c.failed);
      }
    } catch {
      toast.error(c.failed);
    } finally {
      setIsTranslating(false);
    }
  }, [originalMessage, writingLanguage, recipientLanguage, template, c.failed, trackEvent]);

  // Auto-translate on mount
  useEffect(() => {
    translate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isTranslating && !hasTranslated) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-12 text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-sm text-muted-foreground">{c.translating}</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Globe className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{c.title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">{c.subtitle}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <Tabs defaultValue="translated" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="original">
              {c.original} ({LANG_NAMES[writingLanguage]})
            </TabsTrigger>
            <TabsTrigger value="translated">
              {c.translated} ({LANG_NAMES[recipientLanguage]})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="original">
            <div className="bg-muted/30 rounded-lg p-4 min-h-[200px]">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{originalMessage}</p>
            </div>
          </TabsContent>
          <TabsContent value="translated">
            <div className="bg-muted/30 rounded-lg p-4 min-h-[200px]">
              {translatedMessage ? (
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{translatedMessage}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">{c.translating}</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap gap-3 justify-center pt-2">
          <Button
            onClick={() => {
              trackEvent('translation_selected_for_send', { language: recipientLanguage });
              onUseTranslated(translatedMessage);
            }}
            disabled={!translatedMessage || isTranslating}
            className="gap-2"
          >
            <Check className="h-4 w-4" />
            {c.useTranslated}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              trackEvent('original_selected_for_send', { language: writingLanguage });
              onKeepOriginal();
            }}
          >
            {c.keepOriginal}
          </Button>
          <Button
            variant="ghost"
            onClick={onEditOriginal}
          >
            {c.editOriginal}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              trackEvent('translation_regenerated', { from: writingLanguage, to: recipientLanguage });
              translate();
            }}
            disabled={isTranslating}
            className="gap-2"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isTranslating ? 'animate-spin' : ''}`} />
            {c.regenerate}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
